import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@heroui/react';
import { MicOffIcon, VolumeIcon, VolumeOffIcon } from '@/components/icons';
import {
  MAX_VOLUME,
  getUserVolume,
  isLocallyMuted,
  setUserVolume,
  toggleLocalMute,
  useVoiceLocalAudio,
} from '@/lib/voiceLocalAudio';

/** One small participant chip in the focused viewer's bottom strip — click to
 *  switch which participant you're watching (Discord-style). */
export interface ViewerStripEntry {
  userId: string;
  label: string;
  avatarUrl: string | null;
  initial: string;
  speaking: boolean;
  muted: boolean;
  /** Has a live screen/camera stream — clickable to switch the focused view. */
  sharing: boolean;
  /** Currently focused. */
  active: boolean;
}

/**
 * Viewer for one participant's shared screen / camera.
 *
 * It fills the CHANNEL PANE, not the window: opening a stream should not throw
 * you out of the app. The channel list and the member list stay where they
 * are, so you can watch and still see who is talking, switch channels, or read
 * the chat — and a button promotes the viewer to real fullscreen when you do
 * want the whole screen. That two-step is the shape Discord uses and the one
 * this used to skip, jumping straight to a `fixed inset-0` lightbox.
 *
 * Controls auto-hide while the pointer is idle and reveal on movement; a
 * right-click opens a settings menu (stream volume, mute, fullscreen); the
 * bottom strip lists participants so you can switch streams without leaving.
 */
export function ScreenViewer({
  label,
  stream,
  userId,
  strip,
  onSelect,
  onClose,
}: {
  label: string;
  stream: MediaStream | null;
  userId: string;
  strip: ViewerStripEntry[];
  onSelect: (userId: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  useVoiceLocalAudio(); // re-render on volume / mute changes
  const videoWrapRef = useRef<HTMLDivElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Two levels, like Discord: this viewer fills the channel pane (sidebar with
  // channels and members stays put, so you can still see who is talking and
  // switch channels while watching), and from here you can go to real
  // fullscreen. It used to jump straight to a `fixed inset-0` lightbox that
  // swallowed the whole app, which made watching a stream an all-or-nothing
  // mode you had to leave to do anything else.
  const closeRef = useRef<HTMLButtonElement | null>(null);
  // Move focus into the viewer when it opens, or a keyboard user stays parked
  // on the tile behind it and has to Tab through the grid to reach anything
  // here. The close button is the right landing spot: it is the way out.
  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    const root = rootRef.current;
    const sync = () => setIsFullscreen(document.fullscreenElement === root);
    document.addEventListener('fullscreenchange', sync);
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      // Leaving the viewer while still fullscreen would strand the browser in
      // fullscreen with nothing in it.
      if (document.fullscreenElement === root) void document.exitFullscreen?.();
    };
  }, []);

  // Auto-hiding controls: visible on pointer movement, fade out after idle.
  const [controlsOn, setControlsOn] = useState(true);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wake = useCallback(() => {
    setControlsOn(true);
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => setControlsOn(false), 2600);
  }, []);
  useEffect(() => {
    wake();
    return () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [wake]);

  // Right-click settings menu, positioned at the cursor.
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (menu) setMenu(null);
        // In fullscreen the browser handles Esc itself; closing the viewer too
        // would take two steps out of the stream on one keypress.
        else if (document.fullscreenElement !== rootRef.current) onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, menu]);

  const [showStrip, setShowStrip] = useState(true);
  const volume = getUserVolume(userId);
  const muted = isLocallyMuted(userId);
  const fade = controlsOn ? 'opacity-100' : 'opacity-0';
  // Kill clicks on the (invisible) controls while idle, so the faded overlay
  // never swallows a click on the stream.
  const ctl = controlsOn ? 'pointer-events-auto' : 'pointer-events-none';

  function toggleFullscreen() {
    setMenu(null);
    if (document.fullscreenElement === rootRef.current) {
      void document.exitFullscreen?.();
      return;
    }
    // The whole viewer, not the bare <video>: fullscreening the video element
    // alone drops the label, the participant strip and the volume menu, so
    // there is no way to switch who you're watching without leaving again.
    void rootRef.current?.requestFullscreen?.().catch(() => {});
  }

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-label={label}
      // Deliberately NOT aria-modal, and deliberately no focus trap. Both would
      // be right for the old full-window lightbox — they are wrong now: the
      // viewer covers the channel pane only, and the channel list, the member
      // list and the chat stay usable on purpose. Telling assistive tech to
      // ignore everything outside would hide UI that is genuinely still there.
      // In real fullscreen the browser itself confines interaction to this
      // element, so that case needs nothing from us.
      className={
        'absolute inset-0 z-30 flex flex-col bg-black/95 backdrop-blur-md ' +
        (controlsOn ? '' : 'cursor-none')
      }
      onClick={onClose}
      onMouseMove={wake}
    >
      {/* Edge vignette — darkens the screen edges so attention sits on the
          centred stream. Behind the content (painted first); pointer-events
          off so it never blocks clicks. */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse at center, transparent 48%, rgba(0,0,0,0.92) 100%)' }}
      />

      {/* Always-visible exit. The top bar (with members toggle) auto-hides after
          a couple seconds, which left users unsure how to leave the viewer — this
          button never fades. */}
      <button
        ref={closeRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label={t('common.close')}
        title={t('common.close')}
        className="absolute right-3 top-3 z-20 flex h-9 w-9 items-center justify-center rounded-full bg-black/55 text-white ring-1 ring-inset ring-white/15 transition-colors hover:bg-black/80"
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      </button>

      {/* Top bar — label + members toggle. Auto-hides. */}
      <div
        className={
          'pointer-events-none flex shrink-0 items-center justify-between px-4 py-3 text-sm text-white/90 transition-opacity duration-200 ' +
          fade
        }
        onClick={(e) => e.stopPropagation()}
      >
        <span className={'min-w-0 truncate font-medium drop-shadow ' + ctl}>{label}</span>
        <div className="flex shrink-0 items-center gap-2 pr-12">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              toggleFullscreen();
            }}
            title={
              isFullscreen
                ? t('voice.exitFullscreen')
                : t('voice.enterFullscreen')
            }
            aria-label={
              isFullscreen
                ? t('voice.exitFullscreen')
                : t('voice.enterFullscreen')
            }
            className={
              'flex items-center justify-center rounded-full bg-white/10 p-1.5 text-white transition-colors hover:bg-white/20 ' +
              ctl
            }
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              {isFullscreen ? (
                <path d="M9 3v6H3M15 21v-6h6M3 15h6v6M21 9h-6V3" />
              ) : (
                <path d="M8 3H3v5M16 3h5v5M21 16v5h-5M3 16v5h5" />
              )}
            </svg>
          </button>
          {strip.length > 0 && (
            <button
              type="button"
              onClick={() => setShowStrip((v) => !v)}
              title={showStrip ? t('voice.hideMembers') : t('voice.showMembers')}
              className={
                'flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-[12px] text-white transition-colors hover:bg-white/20 ' +
                ctl
              }
            >
              <svg
                viewBox="0 0 24 24"
                width="14"
                height="14"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <span>{showStrip ? t('voice.hideShort') : t('voice.membersShort')}</span>
            </button>
          )}
        </div>
      </div>

      {/* The stream. Right-click anywhere on it opens the settings menu. */}
      <div
        ref={videoWrapRef}
        className="relative mx-6 mb-2 min-h-0 flex-1 overflow-hidden rounded-2xl ring-1 ring-inset ring-white/10"
        onClick={(e) => e.stopPropagation()}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setMenu({ x: e.clientX, y: e.clientY });
          wake();
        }}
      >
        <MediaStreamVideo stream={stream} muted={muted} objectFit="contain" />
      </div>

      {/* Bottom participant strip — switch who you're watching. Auto-hides,
          and can be toggled off entirely (stream grows to fill). */}
      {showStrip && strip.length > 0 && (
        <div
          className={'pointer-events-none flex shrink-0 justify-center px-4 pb-4 pt-2 transition-opacity duration-200 ' + fade}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={ctl + ' flex max-w-full items-end gap-3 overflow-x-auto rounded-2xl bg-black/55 px-3 py-2.5 no-scrollbar'}>
            {strip.map((s) => {
              const ring = s.active
                ? 'ring-2 ring-accent'
                : s.muted
                  ? 'ring-2 ring-danger/60'
                  : s.speaking
                    ? 'ring-2 ring-success'
                    : '';
              return (
                <button
                  key={s.userId}
                  type="button"
                  title={s.label}
                  disabled={!s.sharing}
                  onClick={() => s.sharing && onSelect(s.userId)}
                  className={
                    'flex w-16 shrink-0 flex-col items-center gap-1 transition-opacity ' +
                    (s.sharing ? 'cursor-pointer' : 'cursor-default opacity-90')
                  }
                >
                  {/* Two boxes on purpose. The circle CLIPS — it has to, that
                      is what rounds the photo — so anything living inside it
                      that pokes past the edge gets its corner shaved off. The
                      mute chip did exactly that. It now hangs on this outer
                      box, which has the position but no overflow. */}
                  <div className="relative">
                  <div
                    className={
                      'grid h-12 w-12 place-items-center overflow-hidden rounded-full text-[13px] font-medium text-white transition-[background-color,border-color,color,box-shadow,opacity] ' +
                      ring
                    }
                    style={{
                      backgroundColor: 'rgb(var(--surface-input) / var(--surface-input-alpha))',
                      boxShadow: s.speaking ? '0 0 22px -4px rgb(var(--bk-success) / 0.65)' : undefined,
                    }}
                  >
                    {s.avatarUrl ? (
                      <img src={s.avatarUrl} alt="" className="h-full w-full object-cover" />
                    ) : (
                      s.initial
                    )}
                  </div>
                    {/* 18px chip on a 48px avatar, 12px glyph inside it. It was
                        a 10px `mic-off` — a mic, a stand and a diagonal slash
                        crammed into ten pixels, where the slash lands on a
                        sub-pixel stroke and the whole thing turns to mush. The
                        avatar had the room all along. */}
                    {s.muted && (
                      <span className="absolute -bottom-0.5 -right-0.5 grid size-[18px] place-items-center rounded-full bg-black/80 ring-1 ring-black/40">
                        <MicOffIcon className="size-3 text-danger" />
                      </span>
                    )}
                  </div>
                  <span
                    className={
                      'max-w-[60px] truncate text-[11px] ' + (s.active ? 'text-white' : 'text-white/70')
                    }
                  >
                    {s.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Right-click settings menu (stream volume / mute / pop-out / close). */}
      {menu && (
        <>
          <div className="fixed inset-0 z-[61]" onClick={(e) => { e.stopPropagation(); setMenu(null); }} />
          <div
            className="fixed z-[62] w-60 rounded-xl bg-surface p-3 shadow-soft ring-1 ring-inset ring-separator/60"
            style={{
              left: Math.min(menu.x, window.innerWidth - 250),
              top: Math.min(menu.y, window.innerHeight - 200),
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[12px] font-medium text-ink-muted">
                {t('voice.streamVolume')}
              </span>
              <span className="text-[12px] tabular-nums text-ink">{muted ? 0 : volume}%</span>
            </div>
            <input
              type="range"
              min={0}
              max={MAX_VOLUME}
              step={5}
              value={muted ? 0 : volume}
              onChange={(e) => setUserVolume(userId, Number(e.target.value))}
              className="mb-2 w-full accent-[rgb(var(--bk-accent))]"
            />
            <button
              type="button"
              onClick={() => toggleLocalMute(userId)}
              className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-[13px] text-ink transition-colors hover:bg-default"
            >
              {muted ? <VolumeOffIcon className="h-4 w-4 text-danger" /> : <VolumeIcon className="h-4 w-4" />}
              {muted
                ? t('voice.unmuteStream')
                : t('voice.muteStream')}
            </button>
            <button
              type="button"
              onClick={toggleFullscreen}
              className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-[13px] text-ink transition-colors hover:bg-default"
            >
              <ExpandIcon className="h-4 w-4" />
              {isFullscreen
                ? t('voice.exitFullscreen')
                : t('voice.enterFullscreen')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// In-voice banner. Surfaces an issue blocking the call (mic/camera/screen
// access denied, mediasoup transport failed, etc.). The original was a
// flat danger pill — hard to spot at a glance and lacked any structural
// cue about what to do. This version: bg-surface-secondary rounded-xl border border-separator surface so it reads
// as a "card", MicOffIcon glyph anchored on the left for permission
// errors (mic is the only thing that can be denied in v1 — camera/screen
// share are off by default, screen errors only fire when the user
// initiated the toggle), title + secondary explanation if the message
// has two sentences, otherwise the whole message becomes the title.
export function VoiceErrorBanner(props: {
  message: string;
  micDenied?: boolean;
  onRetryMic?: () => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const [retrying, setRetrying] = useState(false);
  // Split on the first period — title gets the action, description gets
  // the explanation. Falls through to single-line when the message has
  // no clear split.
  const dot = props.message.indexOf('.');
  const title =
    dot > 0 && dot < props.message.length - 1
      ? props.message.slice(0, dot + 1)
      : props.message;
  const description =
    dot > 0 && dot < props.message.length - 1 ? props.message.slice(dot + 1).trim() : '';
  async function handleRetry() {
    if (!props.onRetryMic) return;
    setRetrying(true);
    try {
      await props.onRetryMic();
    } finally {
      setRetrying(false);
    }
  }
  return (
    <div
      className="bg-surface-secondary rounded-xl border border-separator flex w-full max-w-md items-start gap-3 self-center px-4 py-3"
      role="alert"
    >
      <span
        className="grid h-9 w-9 shrink-0 place-items-center rounded-lg"
        style={{
          backgroundColor: 'rgb(var(--bk-danger) / 0.15)',
          color: 'rgb(var(--bk-danger))',
        }}
      >
        <MicOffIcon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1 pt-0.5">
        <p className="text-sm font-light tracking-[-0.015em] text-ink">{title}</p>
        {description && (
          <p className="mt-0.5 text-xs text-ink-muted">{description}</p>
        )}
        {props.micDenied && props.onRetryMic && (
          <button
            type="button"
            onClick={() => void handleRetry()}
            disabled={retrying}
            className="font-medium mt-2 rounded-full bg-accent px-3 py-1 text-[10px] !text-white transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-60"
          >
            {retrying ? t('voice.retrying') : t('voice.retry')}
          </button>
        )}
      </div>
    </div>
  );
}

/** Four corner-arrows "expand to fullscreen" glyph (no icon in the set). */
function ExpandIcon(props: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" className={props.className} aria-hidden>
      <path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3" />
    </svg>
  );
}

export function MediaStreamVideo(props: {
  stream: MediaStream | null;
  muted: boolean;
  objectFit?: 'cover' | 'contain';
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    if (video.srcObject !== props.stream) {
      video.srcObject = props.stream;
    }
    // Detach on unmount — iOS Safari otherwise keeps the MediaStream
    // bound to the element and the track stays "in use" after the React
    // tree is gone, blocking subsequent voice joins on the same device.
    return () => {
      if (video.srcObject) video.srcObject = null;
    };
  }, [props.stream]);

  function goFullscreen() {
    void ref.current?.requestFullscreen?.().catch(() => {});
  }

  return (
    <div className="group/video relative h-full w-full">
      <video
        ref={ref}
        autoPlay
        playsInline
        muted={props.muted}
        className={'h-full w-full ' + (props.objectFit === 'contain' ? 'object-contain' : 'object-cover')}
      />
      {/* Expand to native fullscreen — lets the viewer blow a shared screen
          up to their whole monitor regardless of the tile size. The wrapper
          stops click/pointer bubbling so hitting this control only goes
          fullscreen; it must NOT also trigger the parent tile's "open focus
          lightbox" click handler. */}
      <span
        className="absolute right-2 top-2"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <Button
          isIconOnly
          size="sm"
          variant="ghost"
          aria-label={t('voice.fullscreen')}
          onPress={goFullscreen}
          className="bg-black/40 text-white opacity-0 backdrop-blur-sm transition-opacity hover:bg-black/60 group-hover/video:opacity-100 group-focus-within/video:opacity-100 [@media(pointer:coarse)]:opacity-100"
        >
          <ExpandIcon className="h-4 w-4" />
        </Button>
      </span>
    </div>
  );
}
